import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  List,
  ListItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import type { Control, FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove } from "react-hook-form";
import { useFieldArray, useWatch } from "react-hook-form";
import type { CustomerFormState } from "./CustomerEdit";
import ExpandButton from "../components/entityCard/ExpandButton";
import DeleteIcon from "@mui/icons-material/Delete";
import FormTextField, {
  emailValidationPattern,
  phoneNumberValidationPattern,
} from "../components/formComponents/FormTextField";
import FormSwitch from "../components/formComponents/FormSwitch";
import Phone from "../components/links/Phone";
import Email from "../components/links/Email";

interface PersonProps {
  readonly control: Control<CustomerFormState>;
  readonly index: number;
}

const PersonRow: FC<PersonProps> = ({ control, index }) => {
  const data = useWatch({
    control,
    name: `contacts.${index}`,
  });
  return (
    <TableRow>
      <TableCell>{data.name}</TableCell>
      <TableCell>{data.position}</TableCell>
      <TableCell>{data.phone && <Phone value={data.phone} />}</TableCell>
      <TableCell>{data.email && <Email value={data.email} />}</TableCell>
    </TableRow>
  );
};

interface CollapsedProps {
  readonly values: FieldArrayWithId<CustomerFormState, "contacts", "id">[];
  readonly control: Control<CustomerFormState>;
}

const CollapsedContactPersons: FC<CollapsedProps> = ({ values, control }) => {
  return (
    <Table>
      <TableBody>
        {values.map((v, idx) => (
          <PersonRow key={`${v.name}:${v.phone}:${v.email}`} index={idx} control={control} />
        ))}
      </TableBody>
    </Table>
  );
};

interface ExpandedPersonProps {
  readonly control: Control<CustomerFormState>;
  readonly index: number;
  readonly remove: UseFieldArrayRemove;
}

const ExpandedContactPerson: FC<ExpandedPersonProps> = ({ control, index, remove }) => {
  return (
    <ListItem
      secondaryAction={
        <IconButton edge="end" aria-label="Usuń osobę kontaktową" onClick={() => remove(index)}>
          <DeleteIcon />
        </IconButton>
      }
    >
      <Grid container size={12} spacing={1}>
        <Grid size={6}>
          <FormTextField
            control={control}
            name={`contacts.${index}.name`}
            label="Imię i nazwisko"
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={3}>
          <FormTextField
            control={control}
            name={`contacts.${index}.phone`}
            label="Telefon"
            type="phone"
            rules={{
              pattern: phoneNumberValidationPattern,
            }}
          />
        </Grid>
        <Grid size={3}>
          <FormTextField
            control={control}
            name={`contacts.${index}.email`}
            label="Email"
            type="email"
            rules={{
              pattern: emailValidationPattern,
            }}
          />
        </Grid>
        <Grid size={6}>
          <FormSwitch
            name={`contacts.${index}.privileges.canOrderCalculation`}
            control={control}
            label="Może samodzielnie zamawiać u dostawcy"
          />
          <FormSwitch
            name={`contacts.${index}.privileges.canCreateCalculationFromProducts`}
            control={control}
            label="Może generować kalkulacje z produktów"
          />
        </Grid>
        <Grid size={3}>
          <FormTextField control={control} name={`contacts.${index}.position`} label="Stanowisko" />
        </Grid>
        <Grid size={3}>
          <FormTextField
            control={control}
            name={`contacts.${index}.loginEmail`}
            label="Email do logowania"
            type="email"
            rules={{
              required: true,
              pattern: emailValidationPattern,
            }}
          />
        </Grid>
      </Grid>
    </ListItem>
  );
};

interface ExpandedProps {
  readonly control: Control<CustomerFormState>;
  readonly values: FieldArrayWithId<CustomerFormState, "contacts", "id">[];
  readonly append: UseFieldArrayAppend<CustomerFormState, "contacts">;
  readonly remove: UseFieldArrayRemove;
}

const ExpandedContactPersons: FC<ExpandedProps> = ({ control, values, append, remove }) => {
  return (
    <>
      <List>
        {values.map((v, idx) => (
          <ExpandedContactPerson key={v.id} control={control} index={idx} remove={remove} />
        ))}
      </List>
      <Stack
        sx={{
          alignItems: "flex-end",
        }}
      >
        <Button
          onClick={() =>
            append({
              name: "",
              email: "",
              phone: "",
              loginEmail: "",
              position: "",
              privileges: { canOrderCalculation: false, canCreateCalculationFromProducts: false },
            })
          }
        >
          Dodaj
        </Button>
      </Stack>
    </>
  );
};

interface Props {
  readonly control: Control<CustomerFormState>;
}

const ContactPersons: FC<Props> = ({ control }) => {
  const [expanded, setExpanded] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "contacts",
  });
  return (
    <Card data-testid="contact-persons">
      <CardHeader subheader="Osoby kontaktowe" />
      <CardContent>
        {expanded ? (
          <ExpandedContactPersons control={control} values={fields} append={append} remove={remove} />
        ) : (
          <CollapsedContactPersons control={control} values={fields} />
        )}
      </CardContent>
      <CardActions>
        <ExpandButton expanded={expanded} onChange={setExpanded} />
      </CardActions>
    </Card>
  );
};

export default ContactPersons;
